import { Modal } from 'components/Modal/Modal'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { Button, Stack, Typography } from '@mui/material'
import { colorPrimitives } from 'components/Theme'
import { ActivityButton } from 'components/ActivityButton'
import { type components } from 'api/playerPayback/api'
import { usePostLocationSweepstakeWinnerMessage } from 'hooks/api/usePostLocationSweepstakeWinnerMessage'
import { useSnackbar } from 'stores/useSnackbar'
import { useLayout } from 'hooks/useLayout'

export interface SelectedWinnerType {
  locationName: string
  licenseNumber: string
  winnerName: string
  sweepstakesWinner: components['schemas']['FullSweepstakeWinnerDTO']
  drawingSequence: number
}

interface LocationSweepstakesNotifyWinnersModalProps {
  isModalOpen: boolean
  toggleIsOpen: () => void
  sweepstakeName: string
  selectedWinners: SelectedWinnerType[]
}

export const LocationSweepstakesNotifyWinnersModal = ({
  isModalOpen,
  toggleIsOpen,
  sweepstakeName,
  selectedWinners,
}: LocationSweepstakesNotifyWinnersModalProps) => {
  const { isMobile } = useLayout()
  const heightModal = isMobile ? '100%' : ''
  const setMessage = useSnackbar((state) => state.setMessage)

  const notifyLocationSweepstakeWinners =
    usePostLocationSweepstakeWinnerMessage({
      onSuccess: () => {
        setMessage('Winner has been notified.', 'success')
        toggleIsOpen()
      },
      onError: () => {
        setMessage('Error notifying winner', 'error')
      },
    })

  const handleNotifyLocationSweepstakeWinners = () => {
    notifyLocationSweepstakeWinners.mutate({
      sweepstakeWinnerIds: selectedWinners.map(
        (winner) => winner.sweepstakesWinner.id
      ),
    })
  }

  return (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={toggleIsOpen}
      sx={{
        width: { xs: '90vw', sm: '60%' },
      }}
    >
      <ModalFullHeader
        toggleIsOpen={toggleIsOpen}
        title="Notify Winner"
        subTitle={sweepstakeName}
      />
      <ModalFullBody heightModal={heightModal}>
        <Typography fontSize={18} fontWeight="bold" className="pb-3">
          Are you sure you want to notify this winner they won?
        </Typography>
        <Typography sx={{ color: colorPrimitives.darkGray }} className="pb-6">
          Selecting &quot;Notifiy Winner&quot; will send a message to the winner
          through the Message Center of the J&J Gaming app, as well as an email,
          informing them of their win and providing instructions on how to claim
          their prize.
        </Typography>
        <Stack direction="column">
          {selectedWinners.map((winner) => {
            return (
              <Stack
                columnGap={0.5}
                direction="row"
                key={winner.sweepstakesWinner.id}
                className="pb-4"
              >
                {winner.locationName !== '???' && (
                  <Typography fontWeight="bold">
                    {winner.locationName}
                  </Typography>
                )}
                {winner.licenseNumber !== '#undefined'
                  ? `(${winner.licenseNumber}): ${winner.winnerName}`
                  : `${winner.winnerName}`}
              </Stack>
            )
          })}
        </Stack>
      </ModalFullBody>
      <ModalFullFooter>
        <Button variant="text" onClick={toggleIsOpen}>
          Cancel
        </Button>
        <ActivityButton
          active={notifyLocationSweepstakeWinners.isPending}
          variant="contained"
          onClick={handleNotifyLocationSweepstakeWinners}
        >
          Notify Winner
        </ActivityButton>
      </ModalFullFooter>
    </Modal>
  )
}
